import {Alert, Form, List, Modal, Select, Tag} from 'antd';
import React from 'react';

import {usePostAdminUpdateProductsStatusMutation} from '../../../../gen/api/apis';
import {AdminProductStatus} from '../../../../gen/api/models';
import {useNotification} from '../../../../NotificationContext';
import {productStatuses} from '../../../helpers/labels';
import {productStatusBadge, productStatusLabel} from '../../../helpers/status';

// Statuses that take a product off sale: the server ends its eBay listing published from the admin.
const offSaleStatuses: AdminProductStatus[] = [
    AdminProductStatus.ProductStatusSold,
    AdminProductStatus.ProductStatusReserved,
    AdminProductStatus.ProductStatusTookForThemself,
];

export type ModalStatusProduct = {
    id: string;
    name: string;
    listingNumber: string;
    status: AdminProductStatus;
};

export type BatchStatusChangeModalProps = {
    products: ModalStatusProduct[];
    onClosed: (changed: boolean) => void;
};

export const BatchStatusChangeModal = (props: BatchStatusChangeModalProps) => {
    const notify = useNotification();
    const [form] = Form.useForm();
    const status = Form.useWatch('status', form) as AdminProductStatus | undefined;

    const updateStatus = usePostAdminUpdateProductsStatusMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', `Status changed for ${props.products.length} product(s)`);
            props.onClosed(true);
        },
        onError: (error) => {
            notify.openNotificationWithIcon('error', 'Error changing products status');
            console.error(error);
        },
    });

    const submit = () => {
        form.validateFields().then((values) => {
            updateStatus.mutate({
                params: {
                    status: values.status,
                    productIds: props.products.map((product) => product.id),
                },
            });
        });
    };

    return (
        <Modal
            open
            onCancel={() => props.onClosed(false)}
            onOk={submit}
            title={`Change Status of ${props.products.length} Product(s)`}
            okText="Change Status"
            cancelText="Cancel"
            okButtonProps={{loading: updateStatus.isLoading}}
            width={800}
            centered
            destroyOnClose
        >
            <Form
                layout="vertical"
                form={form}
                name="batchStatusChange"
                onFinish={submit}
            >
                <Form.Item
                    name="status"
                    label="New status"
                    rules={[{required: true, message: 'Please select a status'}]}
                >
                    <Select
                        placeholder="Select a status"
                        options={productStatuses.map((s) => ({
                            value: s.key,
                            label: <Tag color={productStatusBadge(s.key)}>{s.label}</Tag>,
                        }))}
                    />
                </Form.Item>
            </Form>

            {status && offSaleStatuses.includes(status) && (
                <Alert
                    type="warning"
                    showIcon
                    style={{marginBottom: 16}}
                    message="Active eBay listings published from the admin will be ended for these products."
                />
            )}

            <List
                size="small"
                header="Selected products"
                bordered
                style={{maxHeight: 400, overflowY: 'auto'}}
                dataSource={props.products}
                renderItem={(item) => (
                    <List.Item style={{justifyContent: 'flex-start', whiteSpace: 'nowrap'}}>
                        <Tag color={productStatusBadge(item.status)}>{productStatusLabel(item.status)}</Tag>
                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {`[${item.listingNumber}] - ${item.name}`}
                        </span>
                    </List.Item>
                )}
            />
        </Modal>
    );
};
